import React, { useRef, useState } from 'react';
import s from './s.module.less';
import { Prospect, EmailContentItem } from 'types/common';
import { Button, Spin } from 'antd';
import AiTextEditor from 'components/AI/AiTextEditor';
import ProviderStore from 'store/Provider';
import type { HomeInfo } from 'types/provider';
import { EAIEditorType } from 'types/ai';
import SubscriptionStore from 'store/Subscription';
import HomeStore from 'store/Home';
import { TWebSocketListener, useWebSocket } from 'hooks/inbox/useWebSocket';
import { EWSAction, TServerMessage, TWSMessage } from 'types/inbox';
import { handleServerSideMessage, unitAndSortServerMessages } from 'utils/inbox';
import useConversationInfo from 'hooks/useConversationInfo';
import { getUserId } from 'utils/localstore';
import { getConversationMessageList, sendMessage, TGetConversationMessageList } from 'api/inbox';
import InboxMessageList from 'pages/dashboard/InboxPage/MessageContainer/components/InboxMessageList';

type Props = {
    prospect?: Prospect;
    hasBanner: boolean; //means not paid
    homeInfo?: HomeInfo;
};
const DEFAULT_FETCH_MESSAGE_COUNT = 30;
const LOAD_HISTORY_INTERVAL = 1000 * 2;

const Page = (props: Props) => {
    const { prospect, hasBanner } = props;
    const [get] = ProviderStore.useStore();
    const [getSubscription] = SubscriptionStore.useStore();
    const [getHomeSore] = HomeStore.useStore();
    const homeInfo = getHomeSore('data');
    const canSend = homeInfo && (!homeInfo.freeUser || homeInfo.ppaProvider);

    const isSample = prospect?.isSample;

    const [conversation, loadingConversationId] = useConversationInfo(getUserId(), prospect?.id!);

    const conversationMessageMap = React.useRef<Record<string, TServerMessage[] | undefined>>({});
    const selectConversationIdRef = React.useRef<string | undefined>();
    const [messageList, setMessageList] = useState<TServerMessage[]>();
    const [replayContent, setReplayContent] = useState<string>('');
    const [resetFlag, setResetFlag] = useState<{}>({});
    const [loadingConversationMessage, setLoadingConversationMessage] = useState<boolean>(false);
    const latestConversationMessageCursor = React.useRef<Record<string, string>>({});
    const latestLoadHistoryTime = React.useRef<number>(new Date().getTime());
    const allConversationMessageFetchedFlag = React.useRef<Record<string, boolean>>({});
    const [replyingMsg, setReplyingMsg] = useState<boolean>(false); //main pannel
    const providerId = getUserId();
    const loadingHistoryMessageRef = useRef<boolean>(false);
    const [, forceUpdate] = useState<{}>();


    const scrollToLatestMessage = (timeout?: number, smooth?: boolean) => {
        setTimeout(() => {
            const messageWrap = document.getElementById('clientMessageWrap');
            if (messageWrap) {
                messageWrap.scrollTo({
                    top: 99999,
                    behavior: smooth ? 'smooth' : 'auto',
                });
            }
        }, timeout || 150);
    };

    const socketListeners = React.useRef<TWebSocketListener>({
        onOpen: (event) => {
            console.log('WebSocket connection opened:', event);
            // Optionally, you can send a message or perform an action when the connection opens
            // socketRef.current.send(JSON.stringify({ type: 'subscribe', channel: 'inbox' }));
            // resetAutoFetchInterval();
            // refetchConversation();
            // refetchAllUnreadCount();
        },
        onClose: (event) => {
            console.warn('WebSocket connection closed:', event);
            // Optionally, you can try to reconnect here
            // socketRef.current = new WebSocket(WEBSOCKET_URL);
        },
        onMessage: (event) => {
            console.info('WebSocket onMessage:', event);
            const msgData = event.data;
            try {
                const wsMsg: TWSMessage = JSON.parse(msgData);
                if (wsMsg.action === EWSAction.NEWMSG) {
                    const serverMsg = wsMsg.payload as TServerMessage;
                    const conversationIdFromWs = serverMsg.conversationId;
                    const cacheMessageList = conversationMessageMap.current[conversationIdFromWs] || [];
                    let _message = unitAndSortServerMessages([...(cacheMessageList || []), serverMsg]);

                    const currentSortId = serverMsg.sortKey;
                    const targetIndex = cacheMessageList.findIndex((item) => item.sortKey === currentSortId);
                    if (targetIndex >= 0) {
                        cacheMessageList[targetIndex] = { ...serverMsg };
                        _message = unitAndSortServerMessages(cacheMessageList);
                    }

                    conversationMessageMap.current[conversationIdFromWs] = _message || [];
                    // console.log({
                    //     _message,
                    // })
                    if (selectConversationIdRef.current === conversationIdFromWs) {
                        setMessageList(_message);
                        scrollToLatestMessage(undefined, true);
                    }
                } else {
                    console.warn('Unknown WebSocket action:', msgData);
                }
            } catch (e) {
                console.error(e);
            }
        },
        onError: (event) => {
            console.error('WebSocket error:', event);
        },
        autoCloseTime: 1000 * 60 * 10, // 10 minutes
        autoReconnectTime: 1000 * 20, // 20 seconds
    });
    const [socketRef] = useWebSocket(socketListeners.current);


    const fetchMessageByConversationId = async (conversationId: string, cursor?: string, size?: number) => {
        const param: TGetConversationMessageList = {
            conversationId,
            size: size || 50,
        };
        if (cursor) {
            param.cursor = cursor;
        }
        setLoadingConversationMessage(true);
        const res = await getConversationMessageList(param);
        if (!res.error) {
            const data = res?.data?.data?.items || [];
            const isEmpty = data.length === 0;
            const _cursor = res?.data?.data?.cursor;
            const currentCursor = latestConversationMessageCursor.current[conversationId] || '';
            if (!currentCursor || cursor) {
                //mean loading history or loading new but with any cursor
                latestConversationMessageCursor.current[conversationId] = _cursor || '';
            }

            if (isEmpty) {
                allConversationMessageFetchedFlag.current[conversationId] = true;
            }

            const cacheMessageList = conversationMessageMap.current[conversationId] || [];
            const _message = unitAndSortServerMessages([...(cacheMessageList || []), ...data]);
            conversationMessageMap.current[conversationId] = _message || [];
            if (_message && _message[_message.length - 1] && _message[_message.length - 1].aiReplyContent) {
                //last message
                setReplayContent(_message[_message.length - 1].aiReplyContent!);
            }
            setMessageList(_message);
            if (!cursor) {
                scrollToLatestMessage();
            }
        }
        setLoadingConversationMessage(false);
    };

    const loadHistoryMessage = async () => {
        if (loadingHistoryMessageRef.current === true) {
            return;
        }
        const targetConversationId = selectConversationIdRef.current;
        if (!targetConversationId) {
            return;
        }
        const cursor = latestConversationMessageCursor.current[targetConversationId] || '';
        if (!cursor) {
            return;
        }

        const allLoad = allConversationMessageFetchedFlag.current[targetConversationId];
        if (allLoad) {
            return;
        }
        const nowTime = new Date().getTime();
        const latestLoadTime = latestLoadHistoryTime.current;
        const diff = nowTime - latestLoadTime;
        if (diff < LOAD_HISTORY_INTERVAL) {
            return;
        }
        latestLoadHistoryTime.current = nowTime;
        loadingHistoryMessageRef.current = true;
        forceUpdate({});
        await fetchMessageByConversationId(targetConversationId, cursor, DEFAULT_FETCH_MESSAGE_COUNT);
        loadingHistoryMessageRef.current = false;
        forceUpdate({});
    };

    React.useEffect(() => {
        if (!conversation || !conversation.conversationId) {
            return;
        }
        selectConversationIdRef.current = conversation.conversationId;
        fetchMessageByConversationId(conversation.conversationId);
        const messageWrap = document.getElementById('clientMessageWrap');
        const messageScrollCheck = (e: Event) => {
            const target = e.target as HTMLElement;
            const { scrollTop } = target;
            if (scrollTop === 0) {
                loadHistoryMessage();
            }
        };
        if (messageWrap) {
            messageWrap.addEventListener('scroll', messageScrollCheck);
        }

        return () => {
            if (messageWrap) {
                messageWrap.removeEventListener('scroll', messageScrollCheck);
            }
        };
    }, [conversation])

    const onClickReply = async () => {
        // if (!checkContent((replayContent))) {
        //     message.error('Invalid content! Please check the links in your message.');
        //     return;
        // }
        setReplyingMsg(true);
        const res = await sendMessage({
            providerId: providerId!,
            clientId: prospect?.id!,
            message: replayContent.replace(/<[^>]+>/g, ''),
        });
        setReplyingMsg(false);

        if (!res.error) {
            setReplayContent('');
            setResetFlag({});

            //not need
            //fetchMessageByConversationId(selectConversationId!, undefined, 5);
            //add local message
            const cacheMessageList = conversationMessageMap.current[selectConversationIdRef.current!] || [];
            const messageData: TServerMessage = res.data?.data;

            //{\"body\":\"Thank you for reaching out. I am Evan, your healthcare provider. How can I assist you today?\"}
            // const addMessage: TServerMessage = {
            //     senderId: providerId!,
            //     messageId: `${dayjs().unix()}_${Math.random().toString(36).substring(2, 15)}`,
            //     messageType: EMessageType.TEXT,
            //     conversationId: selectConversationId!,
            //     receiverId: targetClient?.id!,
            //     receiverType: EMessageSenderType.PATIENT,
            //     replyToMessageId: undefined,
            //     visibleTo: [EMessageSenderType.PATIENT, EMessageSenderType.PROVIDER],
            //     content: JSON.stringify({
            //         body: replayContent,
            //     }),
            //     senderType: EMessageSenderType.PROVIDER,
            //     createdAt: dayjs().toISOString(),
            //     aiReplyContent: undefined, // AI reply content is not applicable here,
            //     updatedAt: dayjs().toISOString(),
            //     sortKey: `${dayjs().toISOString()}#${Math.random().toString(36).substring(2, 15)}`,
            // }
            const _message = unitAndSortServerMessages([...(cacheMessageList || []), messageData]);
            conversationMessageMap.current[selectConversationIdRef.current!] = _message || [];
            setMessageList(_message);
            scrollToLatestMessage(100, true);
            //end add local message
        }
    };

    const displayMessage = handleServerSideMessage(messageList, 0);

    return (
        <div className={`${s.wrap} ${hasBanner ? s.wrapWithBanner : ''}`}>
            <div className={s.wrapper} id="clientMessageWrap">
                {loadingHistoryMessageRef.current && <Spin className={s.messageLoadingHistory} />}
                <InboxMessageList inboxMessageList={displayMessage} inboxConversation={conversation} />
                {loadingConversationMessage && !loadingHistoryMessageRef.current && <Spin className={s.messageLoading} />}
            </div>

            <div className={s.replyWrap}>
                {/* {isShowEvaTips && !!aiReplyMessageFlag && (
                    <Alert
                        message={
                            <div className={s.text}>
                                🎉 <i className={s.ava} />
                                has drafted a reply for you! Review and click &apos;Send&apos;
                                to reply if it&apos;s ok.
                            </div>
                        }
                        type="info"
                        closable
                        afterClose={() => handleTipsClose()}
                    />
                )} */}

                <AiTextEditor
                    disabled={!canSend || isSample}
                    rows={6}
                    onChange={(e) => {
                        if (typeof e === 'string') {
                            setReplayContent(e as string);
                        } else {
                            setReplayContent(e ? e.target.value : '');
                        }
                    }}
                    value={replayContent}
                    type={EAIEditorType.PROSPECT}
                    resetFlag={resetFlag}
                    maxLength={3000}
                />
                <Button
                    disabled={!replayContent}
                    className={s.btn}
                    type="primary"
                    onClick={onClickReply}
                    loading={replyingMsg}
                >
                    Send
                </Button>
            </div>
        </div>
    );
};

export default Page;
